// Helpers for tracking repeat commenters in the moderation view.
//
// A commenter's count is their lifetime number of farmer comments, computed by the API - it is not affected by
// the other filters on the page. Staff (staff replies, and comments by admin / expert / support accounts) are not
// counted: the API sends no count for them, so they get no badge and never match a count filter.

export const COMMENTER_FILTER_OPTIONS = [
  { value: 'all', label: 'All commenters' },
  { value: 'first', label: 'First-time commenters' },
  { value: 'repeat', label: 'Repeat commenters' },
  { value: 'custom', label: 'More than … comments' },
];

export const COMMENT_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'commentCount', label: 'Most comments first' },
];

/** The "more than N" input as a whole number >= 0, or null when it is empty or not a valid number. */
export const parseMoreThan = (value) => {
  const text = String(value ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  return Number(text);
};

/**
 * Maps the commenter filter selection to the API's minComments / maxComments:
 *  - first-time commenters  -> at most 1 comment
 *  - repeat commenters      -> at least 2 comments
 *  - more than N comments   -> at least N + 1 comments
 * Only the keys that apply are returned.
 */
export const buildCommenterParams = ({ commenterType = 'all', moreThan = '' } = {}) => {
  switch (commenterType) {
    case 'first':
      return { maxComments: 1 };
    case 'repeat':
      return { minComments: 2 };
    case 'custom': {
      const n = parseMoreThan(moreThan);
      return n === null ? {} : { minComments: n + 1 };
    }
    default:
      return {};
  }
};

/**
 * What to show next to a comment's author, or null when there is nothing to show
 * (staff replies, or an API that doesn't send counts yet).
 */
export const getCommenterBadge = (comment) => {
  const count = comment?.commentCount;
  if (count === null || count === undefined) return null;

  const rejected = comment.rejectedCount > 0 ? comment.rejectedCount : 0;
  return count <= 1
    ? { type: 'first', label: 'First-time commenter', count, rejected }
    : { type: 'repeat', label: `Repeat · ${count} comments`, count, rejected };
};
